import {
  IdentityProvider as IDP,
  Language as IdpLanguage,
  useIdentityContext,
  OpCo
} from '@infinitaslearning/il.authentication.avatar';
import { isEmpty, isNil } from 'ramda';
import type { ReactElement, ReactNode } from 'react';
import { config } from '../config/Config';
import type { IConfig } from '../config/Config';
import type { BusinessUnit } from '../utils/business.utils';
import { getBusinessUnit } from '../utils/business.utils';
import { isIdpSessionChecked } from '../utils/idp.utils';
import { IdentityLogin } from './identity-login.component';
import type { IdentityLoginConfig } from './identity-login.component';

export interface IIdpWrapperProps {
  config: IdentityLoginConfig;
  appReady: boolean;
  children?: ReactNode;
}

export function IdpWrapper(props: IIdpWrapperProps) {
  const { config, appReady, children } = props;
  const idContext = useIdentityContext();

  if (idContext.isLoading || !appReady) return null
  if (idContext.isLoggedIn) return <>{children}</>

  return <IdentityLogin config={config} />
}

const mapBusinessUnitToIdpOpco = (businessUnit: BusinessUnit | undefined) => {
  switch (businessUnit) {
    case 'Noordhoff': return OpCo.Noordhoff;
    case 'Liber': return OpCo.Liber;
    case 'Plantyn': return OpCo.Plantyn;
  }
};

export interface IIdentityProviderProps {
  children: ReactNode;
}

const getIdpUrl = (config: IConfig): string => {
  const opco = getBusinessUnit()
  /*
    currently all opco's land on the noordhoff idp when the EDA team has setup
    the correct configurations per opco we need to change this behavior
  */
  switch (opco) {
    case 'Liber': return `${config.idpAuthority.liber}`
    case 'Plantyn': return `${config.idpAuthority.plantyn}`
    case 'Noordhoff': return `${config.idpAuthority.noordhoff}`
    default: return `${config.idpAuthority.noordhoff}`
  }
}

const getScope = (businessUnit: BusinessUnit) => {
  if (businessUnit === 'Noordhoff') {
    return 'openid profile email role unique_name primarysid actOrgId offline_access selectedschool'
  } return 'openid profile email role unique_name primarysid actOrgId offline_access optionalselectedschool'
}

export function IdentityProvider({ children }: IIdentityProviderProps): ReactElement {
  const businessUnit = getBusinessUnit();
  const idpConfig = {
    clientId: config.idpClient,
    idpUrl: getIdpUrl(config),
    externalLogin: config.externalLogin,
    scope: getScope(businessUnit),
    language: IdpLanguage.nlNL,
    logo: `${process.env.PUBLIC_URL}/images/logo.svg`,
    image: `${process.env.PUBLIC_URL}/images/login.jpg`,
    opCo: mapBusinessUnitToIdpOpco(businessUnit),
    postLogoutUrl: window.location.origin + config.basePath,
  } as const;

  function isUsingIdp() {
    return !isNil(config.idpAuthority) && !isEmpty(config.idpAuthority);
  }

  function isAppReady() {
    return !isUsingIdp() || isIdpSessionChecked();
  }

  if (!isUsingIdp()) {
    return <>{children}</>;
  }

  return (
    <IDP {...idpConfig}>
      <IdpWrapper config={idpConfig} appReady={isAppReady()}>
        {children}
      </IdpWrapper>
    </IDP>
  );
}
